// =============================================================================
// STEP 4D: REPLACE FINANCIALS PLACEHOLDER WITH REAL COMPONENT - FINAL STEP
// =============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Box, Typography, Paper, Tabs, Tab, Divider, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Chip, Button
} from '@mui/material';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import AlertSettingsInline from './Alerts/AlertSettingsInline';
import ClientAlertMessages from './Alerts/ClientAlertMessages';
import { calculateClientAlerts, getEffectiveThresholds } from '../utils/alertUtils';

// REAL COMPONENTS - All components now integrated
import ClientInformationView from './ClientInformationView';
import CEOQuestionnaireModule from './CEOQuestionnaireModule';
import UnifiedClientDocumentHistory from './UnifiedClientDocumentHistory';
import ClientFinancialsTab from './ClientFinancialsTab';
import ClientAsanaIntegration from './ClientAsanaIntegration';

export default function ManageClientsPage({ user, clients = [], onClientDataChange, initialState, onAlertDismissed, userAsanaConnection }) {

  const [selectedClientId, setSelectedClientId] = useState('');
  const [activeMainTab, setActiveMainTab] = useState('clientInfo');
  const [activeDocsTab, setActiveDocsTab] = useState('ceoQuestionnaire');

  const [loading, setLoading] = useState(false);
  
  // Alert state
  const [userAlertSettings, setUserAlertSettings] = useState({
    strategicContextDays: 60,
    financialsDays: 90,
    asanaDays: 30
  });
  const [dismissedAlerts, setDismissedAlerts] = useState({});
  const [allClientAlerts, setAllClientAlerts] = useState([]);

  // Check if any client has Asana connected OR user has Asana connected
  const anyClientHasAsana = useMemo(() => {
    const hasUserAsana = !!userAsanaConnection?.accessToken;
    const hasClientAsana = clients.some(c => 
      c.asanaConnection?.accessToken ||
      c.asanaConnection?.status === 'connected' ||
      c.asanaConnection?.lastSyncAt || 
      c.asanaLastSyncAt || 
      c.asanaOperationalBriefUpdatedAt
    );
    return hasUserAsana || hasClientAsana;
  }, [clients, userAsanaConnection]);
  
  const location = useLocation();
  const activeInitialState = initialState || location.state;

  // Effect to handle deep-linking state passed from parent or URL
  useEffect(() => {
    // If the navigation state includes a specific client to select...
    if (activeInitialState?.selectedClientId) {
        setSelectedClientId(activeInitialState.selectedClientId);
    }
    // If the navigation state includes a specific sub-tab to target...
    if (activeInitialState?.targetSubTab) {
        // ...then we know we must first activate the parent "Documents & Questionnaire" tab...
        setActiveMainTab('documents');
        // ...and then activate the specific sub-tab that was requested.
        setActiveDocsTab(activeInitialState.targetSubTab);
    }
  }, [activeInitialState]);

  // NEW: Read deep-link params from URL (e.g., ?selectedClientId=...&targetSubTab=financials)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cid = params.get('selectedClientId') || params.get('clientId');
    const tSub = params.get('targetSubTab');
    const tMain = params.get('targetTab');

    if (cid) {
      setSelectedClientId(cid);
    }
    if (tSub) {
      setActiveMainTab('documents');
      setActiveDocsTab(tSub);
    } else if (tMain === 'documents') {
      setActiveMainTab('documents');
    }
  }, []);

  // Reset selected client if it's no longer in the list
  useEffect(() => {
    if (selectedClientId && !clients.some(c => c.id === selectedClientId)) {
      setSelectedClientId('');
    }
  }, [clients, selectedClientId]);

  // Auto-select the client if there is only one
  useEffect(() => {
    if (clients && clients.length === 1) {
      if (selectedClientId !== clients[0].id) {
        setSelectedClientId(clients[0].id);
      }
    }
  }, [clients, selectedClientId]);

  // Get current client data - MUST be before useEffects that reference it
  const currentClientData = clients.find(c => c.id === selectedClientId);

  // Load user alert settings and dismissed alerts
  useEffect(() => {
    const loadUserAlertData = async () => {
      if (!user?.uid) return;
      
      try {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const data = userSnap.data();
          if (data.alertSettings) {
            setUserAlertSettings(prev => ({ ...prev, ...data.alertSettings }));
          }
          if (data.dismissedAlerts) {
            setDismissedAlerts(data.dismissedAlerts);
          }
        }
      } catch (error) {
        console.error('Error loading user alert data:', error);
      }
    };
    
    loadUserAlertData();
  }, [user?.uid]);

  // Calculate alerts for ALL active clients
  useEffect(() => {
    if (!clients || clients.length === 0) {
      setAllClientAlerts([]);
      return;
    }
    
    const thresholds = getEffectiveThresholds({}, userAlertSettings);
    const allAlerts = [];
    
    clients
      .filter(c => c.isActive === true)
      .forEach(client => {
        const clientAlertResult = calculateClientAlerts(client, thresholds, dismissedAlerts);
        if (clientAlertResult.hasAlerts) {
          // Add client info to each alert
          clientAlertResult.alerts.forEach(alert => {
            allAlerts.push({
              ...alert,
              clientId: client.id,
              clientName: client.name || client.displayName || 'Unnamed Client'
            });
          });
        }
      });
    
    setAllClientAlerts(allAlerts);
  }, [clients, userAlertSettings, dismissedAlerts]);

  // Handle alert dismissal
  const handleDismissAlert = async (clientId, alertType) => {
    if (!user?.uid) return;
    
    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      const currentDismissed = userSnap.exists()
        ? (userSnap.data().dismissedAlerts || {})
        : {};

      const updatedDismissed = {
        ...currentDismissed,
        [clientId]: {
          ...(currentDismissed[clientId] || {}),
          [alertType]: serverTimestamp()
        }
      };

      await updateDoc(userRef, {
        dismissedAlerts: updatedDismissed
      });

      // Update local state immediately for responsive UI
      setDismissedAlerts(prev => ({
        ...prev,
        [clientId]: {
          ...(prev[clientId] || {}),
          [alertType]: new Date()
        }
      }));
      
      // Notify parent to update badge count immediately
      if (onAlertDismissed) {
        onAlertDismissed(clientId, alertType);
      }
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  };

  const handleClientSelect = (clientId) => {
    setSelectedClientId(clientId);
    setActiveMainTab('clientInfo'); // Reset to first tab when changing clients
  };

  const handleMainTabChange = (event, newValue) => {
    setActiveMainTab(newValue);
  };

  const handleDocsTabChange = (event, newValue) => {
    setActiveDocsTab(newValue);
  };

  // Callback to handle client updates from ClientInformationView
  const handleClientUpdate = () => {
    console.log('ManageClientsPage: Client updated, refreshing dashboard data...');
    if (onClientDataChange) {
      onClientDataChange();
    }
  };

  // Count active clients
  const activeClientsCount = useMemo(() => {
  return clients.filter(c => c.isActive === true).length;
}, [clients]);

  // Determine default filter type for document history based on active tab
  const getDocumentHistoryFilterType = () => {
    if (activeMainTab === 'documents') {
      if (activeDocsTab === 'ceoQuestionnaire') {
        return 'Questionnaire';
      } else if (activeDocsTab === 'financials') {
        return 'Financial';
      }
    }
    return null; // Show all documents by default
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, m: 'auto' }}>
      {/* Header Section */}
      <Paper elevation={2} sx={{ p: { xs: 1, sm: 2, md: 3 }, mb: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Client Management
        </Typography>
        
        {/* Three-column layout: Client selector | Alert Settings | Alert Messages */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          alignItems: { xs: 'stretch', md: 'flex-start' }
        }}>
          {/* Column 1: Client count and selector */}
          <Box sx={{ flex: '0 0 auto', minWidth: 200 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              You have {activeClientsCount} active client(s).
            </Typography>
            
            {/* Client Selection - Conditional Display */}
            {clients.length > 1 ? (
              <FormControl fullWidth sx={{ maxWidth: 250 }}>
                <InputLabel>Select Client</InputLabel>
                <Select
                  value={selectedClientId}
                  label="Select Client"
                  onChange={(e) => handleClientSelect(e.target.value)}
                  disabled={loading}
                >
                  <MenuItem value="">
                    <em>-- Select a Client --</em>
                  </MenuItem>
                  {clients.map(client => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name || client.displayName || 'Unnamed Client'}
                      {client.isActive === false ? ' (Inactive)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : clients.length === 1 ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Current Client
                </Typography>
                <Typography variant="h6" component="p">
                  {clients[0].name || clients[0].displayName || 'Unnamed Client'}
                  {clients[0].isActive === false ? ' (Inactive)' : ''}
                </Typography>
              </Box>
            ) : null}
          </Box>

         {/* Column 2: Alert Settings */}
          <Box sx={{ flex: '0 0 auto' }}>
            <AlertSettingsInline userId={user?.uid} showAsana={anyClientHasAsana} />
          </Box>

           {/* Column 3: Alert Messages for ALL clients */}
          {allClientAlerts.length > 0 && (
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <ClientAlertMessages
                alerts={allClientAlerts}
                onDismiss={handleDismissAlert}
                showClientName={true}
              />
            </Box>
          )}
        </Box>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading client data...</Typography>
        </Box>
      )}

      {/* Main Content - Only show if client is selected */}
      {selectedClientId && !loading && currentClientData && (
        <>
          {/* Main Tabs */}
          <Paper elevation={2} sx={{ mb: 3 }}>
            <Tabs
  value={activeMainTab}
  onChange={handleMainTabChange}
  indicatorColor="primary"
>
  <Tab
    label="Client Information"
    value="clientInfo"
    sx={{ color: 'primary.main' }}
  />
  <Tab
    label="Questionnaire & Financials"
    value="documents"
    sx={{ color: 'primary.main' }}
  />
  {/* --- ADD THIS NEW TAB --- */}
  <Tab
    label="Asana"
    value="asana"
    sx={{ color: 'primary.main' }}
  />
  {/* --- END OF NEW TAB --- */}
</Tabs>
            
            <Box sx={{ p: 0 }}>
              {/* Client Information Tab - REAL COMPONENT */}
{activeMainTab === 'clientInfo' && (
<Box>
{/* Phase 4A: Financial Intelligence Tier Status */}
{(() => {
  const hasFull = !!currentClientData?.financialAnalysisSummary?.trim();
  const hasParams =
    !!currentClientData?.financialParameters &&
    Object.keys(currentClientData.financialParameters).length > 0;

  const pendingReprocessing = !!currentClientData?.financialsPendingReprocessing;

  const summaryUpdatedAt = currentClientData?.financialSummaryLastUpdated;
  const pendingSetAt = currentClientData?.financialsPendingReprocessingAt;

  const getMillis = (ts) =>
    !ts ? null :
    typeof ts.toMillis === 'function' ? ts.toMillis() :
    ts instanceof Date ? ts.getTime() :
    typeof ts === 'number' ? ts : null;

  const pendingAlreadyResolved =
    pendingReprocessing &&
    hasFull &&
    getMillis(summaryUpdatedAt) !== null &&
    getMillis(pendingSetAt) !== null &&
    getMillis(summaryUpdatedAt) > getMillis(pendingSetAt);

  const effectivelyPending = pendingReprocessing && !pendingAlreadyResolved;

  // Use Firestore financialIntelligenceTier as source of truth, with fallback inference
  const storedTier = currentClientData?.financialIntelligenceTier;
  const tier = effectivelyPending
    ? 'reprocessing'
    : storedTier === 'ranges_only'
      ? 'ranges_only'
      : storedTier === 'questionnaire_only'
        ? 'questionnaire_only'
        : storedTier === 'full' || hasFull
          ? 'full'
          : hasParams
            ? 'questionnaire_only'
            : 'none';
  const tierConfig = {
    full: { label: '📊 Full Financial Intelligence', color: 'success', description: 'Detailed financial analysis from uploaded documents or QuickBooks.' },
    ranges_only: { label: '📊 Estimated Financial Ranges', color: 'info', description: 'Using estimated financial ranges. Upload detailed financials for deeper analysis.' },
    reprocessing: { label: '🔄 Financials Pending Reprocessing', color: 'warning', description: 'New financial documents uploaded — awaiting admin processing to update analysis.' },
    questionnaire_only: { label: '📋 Questionnaire-Based Intelligence', color: 'warning', description: 'Using CEO questionnaire estimates for financial calibration. Upload financials for deeper analysis.' },
    none: { label: '⚠️ No Financial Context', color: 'default', description: 'No financial data available. Complete the CEO questionnaire or upload financials.' }
  };
  const config = tierConfig[tier];
  return (
    <Box sx={{ mx: 2, mt: 2, mb: 1, p: 2, bgcolor: tier === 'full' ? 'success.50' : tier === 'reprocessing' || tier === 'questionnaire_only' ? 'warning.50' : 'grey.50', borderRadius: 1, border: 1, borderColor: tier === 'full' ? 'success.200' : tier === 'reprocessing' || tier === 'questionnaire_only' ? 'warning.200' : 'grey.300', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Chip label={config.label} color={config.color} variant="filled" size="small" />
        <Typography variant="body2" color="text.secondary">{config.description}</Typography>
      </Box>
            {tier !== 'full' && tier !== 'reprocessing' && tier !== 'ranges_only' && (
        <Button
          variant="outlined"
          size="small"
          color="primary"
          onClick={() => { setActiveMainTab('documents'); setActiveDocsTab('financials'); }}
        >
          Add Financials
        </Button>
      )}
    </Box>
  );
})()}
<ClientInformationView
clientId={selectedClientId}
client={currentClientData}
user={user}
onClientUpdate={handleClientUpdate}
/>
</Box>
)}

{/* Documents & Questionnaire Tab */}
{activeMainTab === 'documents' && (
<Box>
{/* Sub-tabs within Documents & Questionnaire */}
<Tabs
value={activeDocsTab}
onChange={handleDocsTabChange}
variant="fullWidth"
indicatorColor="secondary"
sx={{ borderBottom: 1, borderColor: 'divider' }}
>
<Tab
label="Questionnaire"
value="ceoQuestionnaire"
sx={{ color: 'secondary.main' }}
/>
<Tab
label="Financials"
value="financials"
sx={{ color: 'secondary.main' }}
/>
</Tabs>

{/* Sub-tab Content */}
{activeDocsTab === 'ceoQuestionnaire' && (
<CEOQuestionnaireModule
clientId={selectedClientId}
clientData={currentClientData}
user={user}
/>
)}

{activeDocsTab === 'financials' && (
<ClientFinancialsTab
clientId={selectedClientId}
user={user}
selectedClient={currentClientData}
clientIsActive={currentClientData ? (currentClientData.isActive === true) : false}
/>
)}
</Box>
)}

{/* Asana Tab Content */}
{activeMainTab === 'asana' && (
  <ClientAsanaIntegration 
    selectedClient={currentClientData} 
    user={user} 
  />
)}
            </Box>
          </Paper>
          
          {/* Document History Section - REAL COMPONENT */}
          <Divider sx={{ my: 3 }}>
            <Typography variant="overline">Client Document History</Typography>
          </Divider>
          <UnifiedClientDocumentHistory
            clientId={selectedClientId}
            user={user}
            clientIsActive={currentClientData ? (currentClientData.isActive === true) : false}
            selectedClient={currentClientData}
            defaultFilterType={getDocumentHistoryFilterType()}
          />
        </>
      )}

      {/* No Client Selected State */}
      {!selectedClientId && !loading && (
        <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {clients.length > 0 
              ? "Please select a client to view their details and documents." 
              : "No clients available for this user."
            }
          </Typography>
        </Paper>
      )}
    </Box>
  );
}